// Central keybinding store. One JSON blob in localStorage under
// `smp.keybinds.v1`, with a shallow Action -> string[] map. Movement
// actions default to TWO bindings (WASD + arrow fallback); all other
// actions ship with a single primary binding. The rebind UI only
// rewrites the primary slot; secondary slots persist as defaults.

const STORAGE_KEY = 'smp.keybinds.v1';

export type KeyAction =
  | 'moveUp' | 'moveDown' | 'moveLeft' | 'moveRight'
  | 'hood'   | 'interact' | 'ult'
  | 'ping'   | 'emote'    | 'theme'    | 'pause';

export type BindingMap = Record<KeyAction, string[]>;

export const DEFAULT_BINDINGS: BindingMap = {
  moveUp:    ['KeyW', 'ArrowUp'],
  moveDown:  ['KeyS', 'ArrowDown'],
  moveLeft:  ['KeyA', 'ArrowLeft'],
  moveRight: ['KeyD', 'ArrowRight'],
  hood:      ['KeyH'],
  interact:  ['KeyE'],
  ult:       ['KeyQ'],
  ping:      ['KeyV'],
  emote:     ['KeyB'],
  theme:     ['KeyT'],
  pause:     ['Escape'],
};

export const ACTION_ORDER: KeyAction[] = [
  'moveUp', 'moveDown', 'moveLeft', 'moveRight',
  'hood', 'interact', 'ult',
  'ping', 'emote', 'theme', 'pause',
];

export const ACTION_LABELS: Record<KeyAction, string> = {
  moveUp:    'Move Up',
  moveDown:  'Move Down',
  moveLeft:  'Move Left',
  moveRight: 'Move Right',
  hood:      'Special (Virgin invisibility)',
  interact:  'Interact',
  ult:       'Ultimate',
  ping:      'Ping Wheel',
  emote:     'Emote Wheel',
  theme:     'Cycle Theme',
  pause:     'Pause / Menu',
};

export const ACTION_ICONS: Record<KeyAction, string> = {
  moveUp:    '⬆',
  moveDown:  '⬇',
  moveLeft:  '⬅',
  moveRight: '➡',
  hood:      '🥷',
  interact:  '🖱️',
  ult:       '⚡',
  ping:      '📣',
  emote:     '💃',
  theme:     '🎨',
  pause:     '⏸',
};

type Stored = { version: 1; bindings: Partial<BindingMap> };

let cache: BindingMap | null = null;
const listeners = new Set<() => void>();

function cloneDefaults(): BindingMap {
  return {
    moveUp:    [...DEFAULT_BINDINGS.moveUp],
    moveDown:  [...DEFAULT_BINDINGS.moveDown],
    moveLeft:  [...DEFAULT_BINDINGS.moveLeft],
    moveRight: [...DEFAULT_BINDINGS.moveRight],
    hood:      [...DEFAULT_BINDINGS.hood],
    interact:  [...DEFAULT_BINDINGS.interact],
    ult:       [...DEFAULT_BINDINGS.ult],
    ping:      [...DEFAULT_BINDINGS.ping],
    emote:     [...DEFAULT_BINDINGS.emote],
    theme:     [...DEFAULT_BINDINGS.theme],
    pause:     [...DEFAULT_BINDINGS.pause],
  };
}

function load(): BindingMap {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Stored;
      if (parsed.version === 1 && parsed.bindings) {
        const merged = cloneDefaults();
        for (const k of ACTION_ORDER) {
          const saved = parsed.bindings[k];
          if (Array.isArray(saved) && saved.length > 0) {
            merged[k] = saved.filter((x) => typeof x === 'string');
            if (merged[k].length === 0) merged[k] = [...DEFAULT_BINDINGS[k]];
          }
        }
        cache = merged;
        return cache;
      }
    }
  } catch { /* ignore, fall through to defaults */ }
  cache = cloneDefaults();
  return cache;
}

function save(): void {
  if (!cache) return;
  try {
    const out: Stored = { version: 1, bindings: cache };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(out));
  } catch { /* ignore */ }
  for (const l of listeners) l();
}

export function onChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getBindings(): Readonly<BindingMap> {
  return load();
}

export function getKeys(action: KeyAction): ReadonlyArray<string> {
  return load()[action];
}

// True if `code` is *any* of the keys bound to `action`. Used by main.ts
// keydown handlers, e.g. `isAction(e.code, 'hood')`.
export function isAction(code: string, action: KeyAction): boolean {
  return load()[action].includes(code);
}

// True if any of the currently-held key codes match this action. Used
// by the PlayerController in its per-frame update.
export function isHeldAction(held: ReadonlySet<string>, action: KeyAction): boolean {
  const keys = load()[action];
  for (const k of keys) if (held.has(k)) return true;
  return false;
}

// Rewrite the primary slot for `action`. If another action already holds
// `code` in its primary slot, auto-unbind it from there and return its
// id so the UI can show a toast.
export function setPrimary(action: KeyAction, code: string): { reassignedFrom: KeyAction | null } {
  const bindings = load();
  let reassignedFrom: KeyAction | null = null;
  for (const other of ACTION_ORDER) {
    if (other === action) continue;
    const arr = bindings[other];
    if (arr.length > 0 && arr[0] === code) {
      // Primary slot of `other` held `code` — clear it.
      arr.shift();
      if (arr.length === 0) arr.push(''); // keep shape; empty string = unbound
      reassignedFrom = other;
    }
  }
  const current = bindings[action];
  if (current.length === 0) {
    bindings[action] = [code];
  } else {
    current[0] = code;
  }
  // Clean up any empty-string slots we left behind
  for (const k of ACTION_ORDER) {
    bindings[k] = bindings[k].filter((x) => x !== '');
    if (bindings[k].length === 0) bindings[k] = []; // truly unbound
  }
  save();
  return { reassignedFrom };
}

export function unbind(action: KeyAction): void {
  const b = load();
  b[action] = [];
  save();
}

export function resetToDefaults(): void {
  cache = cloneDefaults();
  save();
}

// Human-readable label for a raw KeyboardEvent.code.
// Falls back to the code itself for unknown entries.
export function formatKeyCode(code: string): string {
  if (!code) return '—';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return 'Num ' + code.slice(6);
  if (code.startsWith('Arrow')) return code.slice(5) + ' Arrow';
  const named: Record<string, string> = {
    Space: 'Space',
    Escape: 'Esc',
    Enter: 'Enter',
    Tab: 'Tab',
    Backspace: 'Backspace',
    ShiftLeft: 'L Shift',
    ShiftRight: 'R Shift',
    ControlLeft: 'L Ctrl',
    ControlRight: 'R Ctrl',
    AltLeft: 'L Alt',
    AltRight: 'R Alt',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
    Backslash: '\\',
    BracketLeft: '[',
    BracketRight: ']',
    Backquote: '`',
    Minus: '-',
    Equal: '=',
  };
  return named[code] ?? code;
}

// Keys we refuse to bind standalone — they're modifiers and would
// prevent chorded shortcuts from ever firing.
export function isModifierOnly(code: string): boolean {
  return (
    code === 'ShiftLeft' || code === 'ShiftRight' ||
    code === 'ControlLeft' || code === 'ControlRight' ||
    code === 'AltLeft' || code === 'AltRight' ||
    code === 'MetaLeft' || code === 'MetaRight'
  );
}
